import React, { useEffect, useState, useRef } from 'react';
import { Edit2, Trash2 } from 'lucide-react';

interface ButtonActionBarProps {
  editor: any;
  onEdit: () => void;
  onDelete: () => void;
  isModalOpen?: boolean;
}

export function ButtonActionBar({ editor, onEdit, onDelete, isModalOpen = false }: ButtonActionBarProps) {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, placement: 'below' as 'below' | 'above' });
  const actionBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateActionBar = () => {
      if (!editor) return;

      const { selection, doc } = editor.state;
      const { from } = selection;

      const node = doc.nodeAt(from);

      if (node && node.type.name === 'emailButton') {
        setShow(true);

        setTimeout(() => {
          const domNode = editor.view.nodeDOM(from);
          if (domNode && domNode instanceof HTMLElement) {
            const rect = domNode.getBoundingClientRect();
            const editorRect = editor.view.dom.getBoundingClientRect();
            const editorContainer = editor.view.dom.closest('.email-content')?.parentElement;
            const containerRect = editorContainer?.getBoundingClientRect() || editorRect;

            const actionBarHeight = 56;
            const spaceBelow = containerRect.bottom - rect.bottom;
            const spaceAbove = rect.top - containerRect.top;

            let placement: 'below' | 'above' = 'below';
            let top = rect.bottom - editorRect.top + 8;

            if (spaceBelow < actionBarHeight && spaceAbove > actionBarHeight) {
              placement = 'above';
              top = rect.top - editorRect.top - actionBarHeight - 8;
            }

            const left = rect.left - editorRect.left + (rect.width / 2);

            setPosition({
              top,
              left,
              placement,
            });
          }
        }, 0);
      } else {
        setShow(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (actionBarRef.current && !actionBarRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-email-button-wrapper]')) {
          setShow(false);
        }
      }
    };

    editor.on('selectionUpdate', updateActionBar);
    editor.on('update', updateActionBar);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside as any);

    return () => {
      editor.off('selectionUpdate', updateActionBar);
      editor.off('update', updateActionBar);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside as any);
    };
  }, [editor]);

  if (!show || isModalOpen) return null;

  return (
    <div
      ref={actionBarRef}
      className="absolute z-50 flex items-center gap-2 bg-white border-2 border-[#FF6B35] rounded-lg shadow-xl p-2 animate-in fade-in slide-in-from-bottom-2 duration-200"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: 'translateX(-50%)',
        minWidth: '200px',
      }}
    >
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onEdit();
        }}
        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 sm:py-2 text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-md transition-colors touch-manipulation"
        title="Button bewerken"
      >
        <Edit2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        <span>Bewerken</span>
      </button>

      <div className="w-px h-8 sm:h-6 bg-gray-300" />

      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete();
        }}
        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 sm:py-2 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded-md transition-colors touch-manipulation"
        title="Button verwijderen"
      >
        <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        <span>Verwijderen</span>
      </button>
    </div>
  );
}
